import PropTypes from 'prop-types';
import _, { isNull } from 'lodash';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAnalytics } from 'use-analytics';
import clsx from 'clsx';
import {
  SearchStatus,
  AutocompleteType,
  SearchModes,
  TravelTypes,
  HomeSearchInputFormVariants,
  checkDoSearchDeepLinkWithoutDate,
  PageLinks,
  TwAttributionVariants,
  SearchInitiators,
} from '../../../../constants';
import formatStateroomsInput from '../../../../helpers/formatStateroomsInput';
import useSearchObjFromQueryString from '../../../../hooks/useSearchObjFromQueryString';
import { autocompleteRequest } from '../../../../store/slices/autocomplete';
import {
  getHotelRequest,
  deepLinkSearchRequest,
  setHotelDetailLoading,
  setIsUserInteractedWithHotelList,
  setHotelRateSearch,
} from '../../../../store/slices/hotel';
import { resetTrips } from '../../../../store/slices/trip';
import {
  setHotelTripSearchInputs,
  setHotelSearchObjFromQueryString,
  resetSearchResults,
  initHotelSearchRequest,
  setTripSearchInputs,
  setHotelTripSearchParam,
} from '../../../../store/slices/tripSearch';
import setURLParams from '../../../../utils/setURLParams';
import CustomerLookup from '../../../CustomerLookup';
import ErrorTooltip from '../../../ErrorTooltip';
import SingleSelectAutocompleteFilter from '../../../Filters/SingleSelectAutocompleteFilter';
import AgentPreferencesFilter from '../../../TravelOption/HotelInformation/HotelFilterBar/AgentPreferencesFilter';
import CheckInDateFilter from '../../../TravelOption/HotelInformation/HotelFilterBar/CheckInDateFilter';
import CheckOutDateFilter from '../../../TravelOption/HotelInformation/HotelFilterBar/CheckOutDateFilter';
import HotelsSearch from '../../../TravelOption/HotelInformation/HotelFilterBar/HotelsSearch';
import RateCodePreferencesFilter from '../../../TravelOption/HotelInformation/HotelFilterBar/RateCodePreferencesFilter';
import RatingHotelFilter from '../../../TravelOption/HotelInformation/HotelFilterBar/RatingHotelFilter';
import HotelRadiusFilter from '../../../TravelOption/HotelInformation/HotelFilterBar/HotelRadiusFilter';
import PassengersFilter from '../../FilterBar/PassengersFilter';
import decodeSearchObjFromQueryString from '../../../../utils/decodeSearchObjFromQueryString';
import {
  checkDoEnableDirectFlightSearch,
  getTravelPolicyLeadDays,
} from '../../../../store/slices/user';
import TwPoweredByLogo from '../../../TwPoweredByLogo';
import checkDoEnableTripConfigDecoupling from '../../../../helpers/selectors/checkDoEnableTripConfigDecoupling';
import getBrowserDateContext from '../../../../helpers/getBrowserDateContext';
import validateHotelSearchInputs, {
  InitialHotelFormValidations,
} from './validateHotelSearchInputs';

const HotelSearchbar = ({ tripId, doShowFlightOriginInput, filterBgColor }) => {
  const isTripSearch = !tripId;
  const ENABLE_CUSTOMER_LOOKUP = false; // Test well when enabling this flag
  const router = useRouter();
  const dispatch = useDispatch();
  const { track } = useAnalytics();
  const { searchInputs, searchObj, searchStatus, searchResult } = useSelector(
    (store) => store.tripSearch,
  );
  const { isLoggedIn, travelAgency, email: loggedInUserEmail } = useSelector((store) => store.user);
  const leadDays = useSelector(getTravelPolicyLeadDays(TravelTypes.hotel));
  const { hotel: hotelSearchInputs, staterooms: hotelStateroomSearchInput } = searchInputs;
  const { hotel: hotelSearchObj, staterooms: hotelStateroomSearchObj } = searchObj;
  const { flightOriginAutocompleteResults } = useSelector((store) => store.autocomplete);
  const selectedSearchMode = useSelector((store) => store.tripSearch.selectedSearchMode);
  const { currencyCode } = useSelector((store) => store.tripSearch);
  const doEnableTripConfigDecoupling = useSelector(checkDoEnableTripConfigDecoupling);
  const { searchMode: searchModeQuery } = router.query;
  const { showUnavailableHotels } = router.query;
  const [flightOriginLabel, setFlightOriginLabel] = useState('Flight origin');
  const [doOpenFirstDatePicker, setDoOpenFirstDatePicker] = useState(false);
  const [doOpenSecondDatePicker, setDoOpenSecondDatePicker] = useState(false);
  const homeSearchInputFormVariant =
    travelAgency && travelAgency.siteTheme && travelAgency.siteTheme.homeSearchInputFormVariant;
  const doCenterAlignFilters =
    !isLoggedIn && homeSearchInputFormVariant === HomeSearchInputFormVariants.CENTER_ALIGNED;
  const gapBetweenSearchbarFields =
    travelAgency && travelAgency.siteTheme && travelAgency.siteTheme.gapBetweenSearchbarFields;
  const doShowGradientOnSearchBtn =
    travelAgency && travelAgency.siteTheme && travelAgency.siteTheme.showGradientOnSearchBtn;

  const twAttributionVariant = travelAgency?.siteTheme?.TwAttributionVariant;
  const doShowAttributionUnderSearchBar =
    twAttributionVariant === TwAttributionVariants.UNDER_SEARCH_BAR;
  const doMakeTravelwitsLogoSmaller = travelAgency?.siteTheme?.doMakeTravelwitsLogoSmaller;

  const handleAutocompleteChange = useCallback(
    (autocompleteTypes, responseType) => (query) => {
      dispatch(autocompleteRequest({ autocompleteTypes, responseType, query }));
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (field) => (value) => {
      dispatch(setHotelTripSearchInputs({ [field]: value }));
    },
    [dispatch],
  );

  const handleChangeCustomer = useCallback(
    (customerInfo) => {
      const currentRooms = hotelStateroomSearchInput;
      const updatedStaterooms = Array.isArray(currentRooms) ? [...currentRooms] : [];
      if (!updatedStaterooms.length) updatedStaterooms.push({});
      updatedStaterooms[0] = {
        ...updatedStaterooms[0],
        customerId: customerInfo && customerInfo.customerId ? customerInfo.customerId : undefined,
        customerEmail: customerInfo && customerInfo.email ? customerInfo.email : undefined,
      };
      dispatch(setTripSearchInputs({ staterooms: updatedStaterooms }));
      dispatch(setHotelTripSearchParam({ staterooms: updatedStaterooms }));
    },
    [dispatch, hotelStateroomSearchInput],
  );

  const [formValidations, setFormValidations] = useState(InitialHotelFormValidations);
  const doAllowDeepLinkWithoutDate =
    travelAgency && travelAgency.settings && travelAgency.settings.doAllowDeepLinkWithoutDate;

  const isDedicatedHotelPage = router.route === PageLinks.DedicatedHotelPage.route;
  const { id } = router.query;
  const hasSearchAddressNotChanged =
    hotelSearchInputs &&
    hotelSearchInputs.selectedAddress &&
    hotelSearchInputs.selectedAddress.value === id;

  const validateSearchAndTrackFailure = (obj, initiator, doShowValidationErrors = true) => {
    const { isValid, reasons } = validateHotelSearchInputs(
      setFormValidations,
      leadDays,
      obj,
      doShowValidationErrors,
    );
    const doTrackFailure = !isValid && initiator !== SearchInitiators.MODE_SWITCH;
    if (doTrackFailure) {
      track('hotelSearch.validationFailed', {
        initiator,
        reasons,
        checkInDate: obj && obj.checkInDate,
        checkOutDate: obj && obj.checkOutDate,
        travelAgencyId: travelAgency?.id,
        ...getBrowserDateContext(),
      });
    }

    return isValid;
  };

  const checkIsNewSearch = () =>
    hotelSearchInputs &&
    hotelSearchObj &&
    (hotelSearchInputs.checkInDate !== hotelSearchObj.checkInDate ||
      hotelSearchInputs.checkOutDate !== hotelSearchObj.checkOutDate ||
      (hotelSearchInputs.selectedAddress || {}).value !==
        (hotelSearchObj.selectedAddress || {}).value ||
      formatStateroomsInput(hotelStateroomSearchInput) !==
        formatStateroomsInput(hotelStateroomSearchObj));

  const handleSearch = (
    event,
    doShowValidationErrors = true,
    initiator = SearchInitiators.SEARCH_BUTTON,
  ) => {
    if (event) event.preventDefault();
    if (validateSearchAndTrackFailure(hotelSearchInputs, initiator, doShowValidationErrors)) {
      dispatch(setIsUserInteractedWithHotelList(false));
      if (
        id &&
        isDedicatedHotelPage &&
        !_.isEmpty(hotelSearchInputs.checkInDate) &&
        !_.isEmpty(hotelSearchInputs.checkOutDate) &&
        hasSearchAddressNotChanged
      ) {
        dispatch(setHotelRateSearch(true));
        dispatch(setHotelDetailLoading(true));
        dispatch(
          getHotelRequest({
            itinerary: {
              hotel: {
                ids: {
                  sabreLodgingId: id,
                },
                checkInStartTime: hotelSearchInputs.checkInDate,
                checkOutEndTime: hotelSearchInputs.checkOutDate,
                rateCodes: hotelSearchInputs.rateCodes,
                exactMatchRateCodesOnly: hotelSearchInputs.exactMatchRateCodesOnly,
                doGetCommissionableRatesOnly: hotelSearchInputs.doGetCommissionableRatesOnly,
                showRefundableHotelRatesOnly: hotelSearchInputs.showRefundableHotelRatesOnly,
              },
            },
            isDedicatedHotelPage: true,
          }),
        );
        setURLParams({
          searchObj: {
            ...searchInputs,
            hotel: {
              ...searchInputs.hotel,
            },
          },
          searchedMode: SearchModes.HOTEL,
          router,
          isDedicatedHotelPage,
        });
        return;
      }
      dispatch(setHotelDetailLoading(true));
      dispatch(resetTrips({ doEnableTripConfigDecoupling }));
      dispatch(resetSearchResults());
      const updatedSearchInputs = {
        ...hotelSearchInputs,
      };

      const isNewSearch = checkIsNewSearch();
      if (isNewSearch) {
        // Check if it's new search, then reset hotel filter values
        updatedSearchInputs.fromPrice = undefined;
        updatedSearchInputs.toPrice = undefined;
        updatedSearchInputs.filterHotelKeys = [];
        updatedSearchInputs.filterHotelChains = [];

        dispatch(setHotelTripSearchInputs({ ...updatedSearchInputs }));
      }
      dispatch(initHotelSearchRequest({ ...updatedSearchInputs, searchProgress: undefined }));
      setURLParams({
        searchObj: {
          ...searchInputs,
          hotel: {
            ...searchInputs.hotel,
            ...updatedSearchInputs,
            selectedCurrency: currencyCode,
          },
        },
        searchedMode: SearchModes.HOTEL,
        router,
        isDedicatedHotelPage: hasSearchAddressNotChanged,
      });
      track('hotelSearch', {
        isNewSearch,
        checkInDate: hotelSearchInputs.checkInDate,
        checkOutDate: hotelSearchInputs.checkOutDate,
        currencyCode,
        travelAgencyId: travelAgency?.id,
        providers: hotelSearchInputs.hotelProvidersToInclude,
        selectedAddress:
          hotelSearchInputs.selectedAddress &&
          `${hotelSearchInputs.selectedAddress.label} (${hotelSearchInputs.selectedAddress.value})`,
      });
    }
  };

  const handleResetValidation = () => {
    setFormValidations(InitialHotelFormValidations);
  };

  useEffect(() => {
    if (isDedicatedHotelPage) {
      const searchObjFromQueryString = decodeSearchObjFromQueryString(
        router.query,
        undefined,
        undefined,
        undefined,
        SearchModes.HOTEL,
      );
      if (searchObjFromQueryString && searchObjFromQueryString.selectedAddress) {
        dispatch(setHotelSearchObjFromQueryString(searchObjFromQueryString));
      }
    }
  }, [dispatch, isDedicatedHotelPage, router.query]);

  useEffect(() => {
    if (checkDoSearchDeepLinkWithoutDate(router.query, doAllowDeepLinkWithoutDate)) {
      const searchObjFromQueryString = decodeSearchObjFromQueryString(
        router.query,
        undefined,
        undefined,
        undefined,
        SearchModes.HOTEL,
      );
      if (searchObjFromQueryString && searchObjFromQueryString.selectedAddress) {
        dispatch(setHotelSearchObjFromQueryString(searchObjFromQueryString));
        dispatch(deepLinkSearchRequest());
        dispatch(
          getHotelRequest({
            itinerary: {
              hotel: {
                ids: {
                  sabreLodgingId: searchObjFromQueryString.selectedAddress.hotelId,
                },
                currencyCode: searchObjFromQueryString.selectedCurrency,
              },
            },
            doSearchHotelWithDeeplink: true,
          }),
        );
      }
    }
  }, [dispatch, doAllowDeepLinkWithoutDate, router.query]);

  useSearchObjFromQueryString((searchObjFromQueryString) => {
    if (searchStatus !== SearchStatus.INITIAL || !travelAgency) return;
    if ((searchModeQuery || SearchModes.CRUISE_TRIP) === selectedSearchMode) {
      dispatch(setHotelSearchObjFromQueryString(searchObjFromQueryString));
      if (
        isDedicatedHotelPage === false &&
        !checkDoSearchDeepLinkWithoutDate(router.query, doAllowDeepLinkWithoutDate)
      ) {
        if (
          validateSearchAndTrackFailure(
            searchObjFromQueryString,
            SearchInitiators.RESULTS_PAGE_URL,
            true,
          )
        ) {
          dispatch(initHotelSearchRequest({ searchProgress: undefined }));
        }
      }
    } else {
      dispatch(resetSearchResults({ isLoggedIn }));
      handleSearch(null, false, SearchInitiators.MODE_SWITCH);
    }
  }, undefined);

  useEffect(() => {
    if (searchStatus === SearchStatus.DONE) {
      const travelOptions = searchResult?.travelOptions ?? [];
      const travelOption = travelOptions.length && searchResult.travelOptions[0];

      const groupedByProvider = _.groupBy(
        travelOptions,
        (option) => option?.hotelItinerary?.hotel?.priceProvider || 'unknown',
      );
      const providerCounts = {};
      Object.keys(groupedByProvider).forEach((provider) => {
        providerCounts[`${provider}Hotels`] = groupedByProvider[provider].length;
      });

      track(
        'receivedHotels',
        !searchResult
          ? undefined
          : {
              doHaveMoreHotels: searchResult.doHaveMoreHotels,
              travelAgencyId: travelAgency?.id,
              loggedInUserEmail,
              ...providerCounts,
              totalFound: searchResult.total,
              searchInputs: !travelOption
                ? undefined
                : {
                    checkInDate: travelOption.startDateTime,
                    checkOutDate: travelOption.endDateTime,
                    selectedAddress:
                      travelOption.hotelItinerary && travelOption.hotelItinerary.searchGeoname
                        ? `${travelOption.hotelItinerary.searchGeoname.name} (${travelOption.hotelItinerary.searchGeoname.googlePlaceId})`
                        : undefined,
                  },
            },
      );
    }
    // oxlint-disable-next-line react/exhaustive-deps
  }, [searchStatus]);

  useEffect(() => {
    if (
      travelAgency &&
      travelAgency.defaultShowAgencyPreferredHotels &&
      searchInputs.hotel.doShowOnlyAgentPreferredHotels === undefined
    ) {
      dispatch(setHotelTripSearchInputs({ doShowOnlyAgentPreferredHotels: true }));
    }
  }, [dispatch, searchInputs.hotel.doShowOnlyAgentPreferredHotels, travelAgency]);

  useEffect(() => {
    const doShowAvailableHotelsOnly =
      !travelAgency ||
      !travelAgency.filterSettings ||
      travelAgency.filterSettings.doShowAvailableHotelsOnly;
    if (
      searchInputs.hotel.doShowAvailableHotelsOnly === undefined &&
      showUnavailableHotels === undefined
    ) {
      dispatch(setHotelTripSearchInputs({ doShowAvailableHotelsOnly }));
    } else {
      dispatch(
        setHotelTripSearchInputs({
          doShowAvailableHotelsOnly: searchInputs.hotel.doShowAvailableHotelsOnly,
        }),
      );
    }
  }, [dispatch, searchInputs.hotel.doShowAvailableHotelsOnly, travelAgency, showUnavailableHotels]);

  useEffect(() => {
    if (
      travelAgency &&
      travelAgency.filterSettings &&
      travelAgency.filterSettings.flightOriginLabel
    ) {
      setFlightOriginLabel(travelAgency.filterSettings.flightOriginLabel);
    }
  }, [travelAgency]);

  const handleOpenFirstDatePicker = useCallback((val) => {
    setDoOpenFirstDatePicker(val);
    if (!val) {
      setTimeout(() => {
        setDoOpenSecondDatePicker(true);
      }, 50);
    }
  }, []);

  useEffect(() => {
    if (doOpenFirstDatePicker) setDoOpenSecondDatePicker(false);
  }, [doOpenFirstDatePicker]);

  return (
    <form onSubmit={handleSearch} onFocus={handleResetValidation}>
      <div className="mt-2">
        <div
          className={`grid xl:grid-cols-10 md:grid-cols-12 grid-cols-1 mt-2 px-0 py-2 md:p-0 gap-y-2 ${gapBetweenSearchbarFields}`}
        >
          <ErrorTooltip
            title={formValidations.selectedAddress || ''}
            open={!isNull(formValidations.selectedAddress)}
            arrow
          >
            <div className="xl:col-span-4 col-span-4">
              <HotelsSearch tripId={tripId} doUseThemeBackground />
            </div>
          </ErrorTooltip>
          <ErrorTooltip
            title={formValidations.checkInDate || ''}
            open={!isNull(formValidations.checkInDate)}
            arrow
          >
            <div className="xl:col-span-2 col-span-3">
              <CheckInDateFilter
                tripId={tripId}
                doOpenDatePicker={doOpenFirstDatePicker}
                setDoOpenDatePicker={handleOpenFirstDatePicker}
                bgClassName="bg-searchInput-0"
              />
            </div>
          </ErrorTooltip>
          <ErrorTooltip
            title={formValidations.checkOutDate || ''}
            open={!isNull(formValidations.checkOutDate)}
            arrow
          >
            <div className="xl:col-span-2 col-span-3">
              <CheckOutDateFilter
                tripId={tripId}
                doOpenDatePicker={doOpenSecondDatePicker}
                setDoOpenDatePicker={setDoOpenSecondDatePicker}
                bgClassName="bg-searchInput-0"
              />
            </div>
          </ErrorTooltip>
          <div className="col-span-2">
            <button
              type="submit"
              className={clsx(
                'md:rounded-r-2xl md:rounded-l-none rounded-l-2xl rounded-r-2xl bg-searchButton-0 text-searchbtn-0 font-bold text-center w-full h-16 md:h-full',
                { 'hover:gradient-animation': doShowGradientOnSearchBtn },
              )}
            >
              <span className="font-body">Search</span>
            </button>
          </div>
        </div>
        <div
          className={clsx('flex gap-2 mt-4 flex-wrap text-white', {
            'justify-center': doCenterAlignFilters,
          })}
        >
          <PassengersFilter bgClassName={filterBgColor} />
          {(!travelAgency ||
            !travelAgency.tripkitEnabledTransportModes ||
            travelAgency.tripkitEnabledTransportModes.indexOf(TravelTypes.flight) > -1) &&
            !hasSearchAddressNotChanged &&
            doShowFlightOriginInput && (
              <SingleSelectAutocompleteFilter
                onChange={handleFilterChange('origin')}
                options={flightOriginAutocompleteResults}
                onInputChange={handleAutocompleteChange(
                  AutocompleteType.FLIGHT_ORIGIN,
                  'flightOriginAutocompleteResults',
                )}
                value={hotelSearchInputs.origin}
                label={flightOriginLabel}
                filterButtonMaxWidth="max-w-filter"
                widthInside="max-w-85"
                bgClassName={filterBgColor}
              />
            )}
          {ENABLE_CUSTOMER_LOOKUP && isTripSearch && (
            <CustomerLookup doShowInHeaderFilter onChange={handleChangeCustomer} />
          )}
          <AgentPreferencesFilter tripId={tripId} variant={null} filterBgColor={filterBgColor} />
          <RateCodePreferencesFilter tripId={tripId} variant={null} filterBgColor={filterBgColor} />
          {!hasSearchAddressNotChanged && (
            <RatingHotelFilter tripId={tripId} variant={null} filterBgColor={filterBgColor} />
          )}
          <HotelRadiusFilter tripId={tripId} variant={null} filterBgColor={filterBgColor} />
          {doShowAttributionUnderSearchBar && (
            <TwPoweredByLogo doMakeSmaller={doMakeTravelwitsLogoSmaller} />
          )}
        </div>
      </div>
    </form>
  );
};

HotelSearchbar.propTypes = {
  tripId: PropTypes.string,
  doShowFlightOriginInput: PropTypes.bool,
  filterBgColor: PropTypes.string,
};

HotelSearchbar.defaultProps = {
  doShowFlightOriginInput: true,
  tripId: undefined,
  filterBgColor: undefined,
};
export default HotelSearchbar;
